import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Pencil, Trash2, ShieldCheck, ShieldAlert, Building2, Wrench } from "lucide-react"
import { useEquipmentItem, useDeleteEquipment } from "@/hooks/use-equipment"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { formatDate } from "@/lib/utils"
import { toast } from "sonner"

export function EquipmentDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useEquipmentItem(id)
  const deleteMutation = useDeleteEquipment()

  const eq = data?.data

  const handleDelete = async () => {
    if (!id || !confirm("Czy na pewno chcesz usunąć to urządzenie?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Urządzenie usunięte")
    navigate("/equipment")
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!eq) {
    return <div className="text-muted-foreground">Nie znaleziono urządzenia</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/equipment")} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-foreground">{eq.name}</h1>
              {eq.category && <Badge variant="secondary" className="text-xs">{eq.category}</Badge>}
            </div>
            {eq.manufacturer && (
              <p className="text-sm text-muted-foreground">
                {eq.manufacturer} {eq.model_name || ""}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/equipment/${id}/edit`)}>
            <Pencil size={14} /> Edytuj
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600">
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main */}
        <div className="col-span-2 space-y-4">
          {/* Info */}
          <div className="rounded-lg border border-border p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Szczegóły</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Nr seryjny</span>
                <p className="font-mono text-foreground">{eq.serial_number || "—"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Producent</span>
                <p className="text-foreground">{eq.manufacturer || "—"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Model</span>
                <p className="text-foreground">{eq.model_name || "—"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Data zakupu</span>
                <p className="text-foreground">{eq.purchase_date ? formatDate(eq.purchase_date) : "—"}</p>
              </div>
            </div>
          </div>

          {/* Warranty */}
          <div
            className={`rounded-lg border p-4 flex items-center gap-3 ${eq.warranty_active ? "border-green-500 bg-green-500/5" : eq.warranty_until ? "border-red-500 bg-red-500/5" : "border-border"}`}
          >
            {eq.warranty_active ? (
              <ShieldCheck size={20} className="text-green-500" />
            ) : (
              <ShieldAlert size={20} className={eq.warranty_until ? "text-red-500" : "text-muted-foreground"} />
            )}
            <div>
              <p className="text-sm font-medium text-foreground">
                {eq.warranty_active ? "Gwarancja aktywna" : eq.warranty_until ? "Gwarancja wygasła" : "Brak danych o gwarancji"}
              </p>
              {eq.warranty_until && (
                <p className="text-xs text-muted-foreground">
                  {eq.warranty_active ? "Do:" : "Wygasła:"} {formatDate(eq.warranty_until)}
                </p>
              )}
            </div>
          </div>

          {eq.description && (
            <div className="rounded-lg border border-border p-4 space-y-2">
              <h2 className="text-sm font-semibold text-foreground">Opis</h2>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{eq.description}</p>
            </div>
          )}

          {/* Service tickets */}
          {eq.service_tickets && eq.service_tickets.length > 0 && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <h2 className="text-sm font-semibold text-foreground">
                <Wrench size={14} className="inline mr-1.5" />
                Zgłoszenia serwisowe ({eq.service_tickets.length})
              </h2>
              <div className="space-y-2">
                {eq.service_tickets.map((t) => (
                  <div
                    key={t.id}
                    onClick={() => navigate(`/service/${t.id}`)}
                    className="flex items-center gap-3 rounded-md border border-border px-3 py-2 cursor-pointer transition-colors hover:bg-muted/30"
                  >
                    <div className="h-2 w-2 rounded-full" style={{ backgroundColor: t.phase.color }} />
                    <span className="flex-1 text-sm text-foreground">{t.title}</span>
                    <Badge variant="outline" className="text-[10px]">{t.phase.name}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="rounded-lg border border-border p-4 space-y-3">
            {eq.company && (
              <div>
                <span className="text-xs text-muted-foreground">Kontrahent</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <Building2 size={14} className="text-muted-foreground" />
                  <p className="text-sm text-foreground">{eq.company.name}</p>
                </div>
              </div>
            )}
            <div>
              <span className="text-xs text-muted-foreground">Status</span>
              <Badge variant={eq.is_active ? "secondary" : "outline"} className="mt-0.5 text-xs">
                {eq.is_active ? "Aktywny" : "Nieaktywny"}
              </Badge>
            </div>
            <div className="border-t border-border pt-3">
              <span className="text-xs text-muted-foreground">Dodano</span>
              <p className="text-sm text-foreground">{formatDate(eq.created_at)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
